import React, { useState, useEffect, useRef, useMemo } from "react";
import classnames from "classnames";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useMediaQuery, mediaQueries } from "hooks/useMediaQuery";
import { Link } from "components/core/Link";
import { ClickToCall } from "components/brand/ClickToCall";
import {
	getStorageUnitsNearMe,
	storageTypes,
	dealsLink
} from "config/headerMenuLinks";
import HeaderMenu from "components/brand/Header/HeaderMenu";
import { Chevron, PriceTag } from "components/core/Icons";
import Logo from "assets/images/full-logo.svg";
import { useTollfreeNumber } from "hooks/useTollfreeNumber";
import "./Header.scss";

const MOBILE_MENU_TOGGLE_ID = "mobile-menu-toggle";

function Header() {
	const [belowLarge] = useMediaQuery([mediaQueries.belowLarge]);
	const [expanded, setExpanded] = useState(false);
	// the name of the currently open menu, enables toggling between open menus
	const [openMenuName, setOpenMenuName] = useState(null);
	const handleExpand = (event) => setExpanded(event.target.checked);
	const useMobileMenu = expanded && belowLarge;
	const { phone, handleFetchNumber } = useTollfreeNumber();
	const headerRef = useRef(null);
	const searchDrilldownLinks = useSelector(
		(state) => state.search.content?.drilldownLinks ?? null
	);
	const { pathname } = useLocation();
	// Matches /search/, /storage-units-near-me/:keyword/, and /self-storage/:state/:city/
	// (plus its amenity-slug children) - the routes that render search results.
	const isSearchPage =
		pathname.startsWith("/search/") ||
		pathname.startsWith("/storage-units-near-me/") ||
		/^\/self-storage\/[^/]+\/[^/]+\//.test(pathname);
	const facilityId = useSelector(
		(state) => state.facilities?.currentFacility?.id
	);
	// The facility API response already carries its own city-scoped drilldownLinks
	// (same data as the CLP), so facility pages read it from there instead of
	// firing a second, heavier /api/search/landing request just for this menu.
	const facilityDrilldownLinks = useSelector((state) =>
		facilityId ? state.facilities.byId[facilityId]?.drilldownLinks : null
	);
	const drilldownLinks = facilityId
		? facilityDrilldownLinks
		: searchDrilldownLinks;
	// Facility pages have no dedicated pageType, so they're gated on facilityId instead of isSearchPage.
	const isClpPage = isSearchPage || Boolean(facilityId);
	const storageUnitsNearMe = useMemo(
		() => getStorageUnitsNearMe(isClpPage ? drilldownLinks : null),
		[isClpPage, drilldownLinks]
	);

	useEffect(() => {
		if (!openMenuName && !expanded) return undefined;

		const handleClickOutside = (event) => {
			if (
				headerRef.current &&
				!headerRef.current.contains(event.target)
			) {
				setOpenMenuName(null);
				setExpanded(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("touchstart", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("touchstart", handleClickOutside);
		};
	}, [openMenuName, expanded, belowLarge]);

	return (
		<div
			className="header"
			ref={headerRef}
		>
			<div className="left-section">
				<Link
					last
					className="logo-link"
					href="/"
					underline={false}
				>
					<img
						className="icon svg logo"
						alt="icon"
						src={Logo}
						width="206"
						height="45"
					/>
				</Link>
				{/* Checkbox hack (https://css-tricks.com/the-checkbox-hack/) so
				the mobile nav opens/closes natively via CSS :has(:checked)
				when JS is disabled; rendered unconditionally (CSS's
				.menu-button hides it above the large breakpoint) since
				belowLarge itself only ever resolves via JS. onChange below is
				a progressive enhancement for isStacked and outside-click-close. */}
				<label
					htmlFor={MOBILE_MENU_TOGGLE_ID}
					title={`${
						expanded ? "Close" : "Open"
					} main navigation menu`}
					className={classnames("menu-button", "button-menu", {
						"button-expanded": expanded,
						"button-collapsed": !expanded
					})}
				>
					<Chevron rotate={expanded ? "up" : "down"} />
					<input
						type="checkbox"
						id={MOBILE_MENU_TOGGLE_ID}
						aria-label={`${
							expanded ? "Close" : "Open"
						} main navigation menu`}
						className="visually-hidden"
						checked={expanded}
						onChange={handleExpand}
					/>
				</label>
			</div>
			<div className="dropdown-menu">
				{[storageUnitsNearMe, storageTypes].map(
					({ menuName, links, sections }) => (
						<HeaderMenu
							key={menuName}
							isStacked={useMobileMenu}
							menuName={menuName}
							links={links}
							sections={sections}
							openMenuName={openMenuName}
							setOpenMenuName={setOpenMenuName}
						/>
					)
				)}
				<Link
					last
					className="link-deals"
					href={dealsLink.href}
					icon={<PriceTag size="medium" />}
				>
					{dealsLink.text}
				</Link>
			</div>
			<div className="right-section">
				<ClickToCall
					handleFetchNumber={handleFetchNumber}
					phone={phone}
				/>
			</div>
		</div>
	);
}

export default Header;
